import React, { useState, useEffect } from 'react';
import {
  gramToKg,
  validateAndNormalizeQuantity,
  formatQuantityDisplay} from '../../utils/unitHelpers';

/**
 * UnitSelector - Gram/Kilogram seçimi ve miktar kontrolü bileşeni
 * 
 * @param {Object} props
 * @param {number} props.saleType - Satış tipi (0: Adet, 1: Gram/Kg)
 * @param {number} props.minimumOrderWeight - Minimum sipariş ağırlığı (gram cinsinden)
 * @param {number} props.price - Birim fiyat (kg başına)
 * @param {number|null} props.discountedPrice - İndirimli fiyat
 * @param {function} props.onQuantityChange - Miktar değişim callback (quantity, unit)
 * @param {number} props.initialQuantity - Başlangıç miktarı
 * @param {string} props.initialUnit - Başlangıç birimi ('gram', 'kg', 'piece')
 * @param {boolean} props.showPrice - Fiyat gösterilsin mi?
 * @param {string} props.size - Boyut ('sm', 'md', 'lg')
 */
const UnitSelector = ({
  saleType = 0,
  minimumOrderWeight = 0,
  price = 0,
  discountedPrice = null,
  onQuantityChange,
  initialQuantity = 1,
  initialUnit = 'piece',
  showPrice = false,
  size = 'md'
}) => {
  // Satış tipi adet ise, birim her zaman 'piece', gram bazlı ise initialUnit'a göre
  const unit = saleType === 0 ? 'piece' : initialUnit;
  
  const [quantity, setQuantity] = useState(initialQuantity);
  const [error, setError] = useState(null);

  // Minimum miktar
  const minimumQuantity = saleType === 1 ? 
    (unit === 'kg' ? minimumOrderWeight / 1000 : minimumOrderWeight) : 1;

  // initialQuantity prop değiştiğinde state'i güncelle
  useEffect(() => {
    setQuantity(initialQuantity);
  }, [initialQuantity]);

  // Component mount edildiğinde parent'a ilk değeri bildir
  useEffect(() => {
    if (onQuantityChange) {
      onQuantityChange(quantity, unit);
    }
  }, []);

  // Miktar değişimini valide et ve parent'a bildir
  const handleQuantityChange = (newQuantity, newUnit = unit) => {
    const validation = validateAndNormalizeQuantity(newQuantity, newUnit, minimumOrderWeight);
    
    if (!validation.isValid) {
      setError(validation.errorMessage);
      setQuantity(validation.normalizedQuantity);
      
      if (onQuantityChange) {
        onQuantityChange(validation.normalizedQuantity, newUnit);
      }
      
      // Hata mesajını 3 saniye sonra temizle
      setTimeout(() => setError(null), 3000);
    } else {
      setError(null);
      setQuantity(newQuantity);
      
      if (onQuantityChange) {
        onQuantityChange(newQuantity, newUnit);
      }
    }
  };


  // Artır
  const handleIncrement = () => {
    let step = 1;
    if (unit === 'gram') {
      step = 250;
    } else if (unit === 'kg') {
      step = 0.25;
    }
    const newQuantity = Number((quantity + step).toFixed(unit === 'kg' ? 2 : 0));
    handleQuantityChange(newQuantity);
  };

  // Azalt
  const handleDecrement = () => {
    let step = 1;
    if (unit === 'gram') {
      step = 250;
    } else if (unit === 'kg') {
      step = 0.25;
    }
    const newQuantity = Number((quantity - step).toFixed(unit === 'kg' ? 2 : 0));
    const finalQuantity = Math.max(newQuantity, minimumQuantity);
    handleQuantityChange(finalQuantity);
  };

  // Manual input
  const handleInputChange = (e) => {
    const value = e.target.value;
    
    // Boş string kontrolü
    if (value === '') {
      setQuantity('');
      return;
    }
    
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      // Eğer kg cinsinden girildiyse gram'a çevir
      const displayInfo = formatQuantityDisplay(quantity);
      if (displayInfo.unit === 'kg' && numValue < 1000) {
        // Kullanıcı kg cinsinden değer girdi, gram'a çevir
        handleQuantityChange(numValue * 1000);
      } else {
        // Gram cinsinden değer girdi
        handleQuantityChange(numValue);
      }
    }
  };

  // Input blur - validation
  const handleInputBlur = () => {
    if (quantity === '' || quantity < minimumQuantity) {
      handleQuantityChange(minimumQuantity);
    }
  };

  // Fiyat hesaplama
  const calculateTotalPrice = () => {
    const basePrice = discountedPrice || price;
    
    if (saleType === 0) {
      return basePrice * quantity;
    }
    
    // Gram bazlı satış - fiyat kg başına
    let quantityInKg = quantity;
    if (unit === 'gram') {
      quantityInKg = gramToKg(quantity);
    }
    return basePrice * quantityInKg;
  };

  // Size class'ları
  const sizeClasses = {
    sm: 'h-36 w-36 text-sm',
    md: 'h-48 w-48',
    lg: 'h-56 w-56 text-lg'
  };

  const buttonSizeClass = sizeClasses[size] || sizeClasses.md;

  // Adet bazlı satış için basit kontrol
  if (saleType === 0) {
    return (
      <div className="unit-selector">
        <div className="d-flex align-items-start justify-content-start">
          <div className="quantity-control d-flex align-items-center border border-gray-200 rounded-4 overflow-hidden bg-white">
            <button
              type="button"
              onClick={handleDecrement}
              className={`quantity-btn quantity-minus d-flex align-items-center justify-content-center border-0 bg-white text-gray-600 hover-bg-main-600 hover-text-white transition-all ${buttonSizeClass}`}
              disabled={quantity <= minimumQuantity}
              style={{ minWidth: '36px', minHeight: '36px' }}
            >
              <i className="ph ph-minus" style={{ fontSize: '14px' }} />
            </button>
            <div className="quantity-display d-flex align-items-center justify-content-center px-3 bg-white border-start border-end border-gray-200">
              <span className="text-gray-900 fw-medium text-sm">
                {quantity} Adet
              </span>
            </div>
            <button
              type="button"
              onClick={handleIncrement}
              className={`quantity-btn quantity-plus d-flex align-items-center justify-content-center border-0 bg-white text-gray-600 hover-bg-main-600 hover-text-white transition-all ${buttonSizeClass}`}
              style={{ minWidth: '36px', minHeight: '36px' }}
            >
              <i className="ph ph-plus" style={{ fontSize: '14px' }} />
            </button>
          </div>
        </div>
        
        {showPrice && (
          <div className="mt-12">
            <span className="text-main-600 fw-bold" style={{ fontSize: size === 'sm' ? '16px' : '20px' }}>
              ₺{calculateTotalPrice().toFixed(2)}
            </span>
          </div>
        )}
        
        {error && (
          <div className="alert alert-danger alert-sm mt-12 mb-0 py-8 px-12" role="alert">
            <i className="ph ph-warning-circle me-4"></i>
            {error}
          </div>
        )}

        <style jsx>{`
          .unit-selector {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }

          .quantity-control {
            border-radius: 8px !important;
            transition: all 0.2s ease;
            border: 1px solid #e0e0e0 !important;
            background: white !important;
          }

          .quantity-btn {
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
            background: white !important;
            color: #6c757d !important;
          }

          .quantity-btn:hover:not(:disabled) {
            background: var(--main-600, #299E60) !important;
            color: white !important;
          }

          .quantity-btn:active {
            transform: translateY(0);
          }

          .quantity-display {
            min-width: 100px;
            min-height: 36px;
            border-radius: 0;
            border-left: 1px solid #e0e0e0 !important;
            border-right: 1px solid #e0e0e0 !important;
            background: white !important;
          }

          .quantity-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            background: #f8f9fa !important;
            color: #adb5bd !important;
          }

          .quantity-btn:disabled:hover {
            background: #f8f9fa !important;
            color: #adb5bd !important;
            transform: none !important;
          }

          /* Responsive adjustments */
          @media (max-width: 576px) {
            .quantity-display {
              min-width: 80px;
              font-size: 12px;
            }
            
            .quantity-btn {
              min-width: 32px !important;
              min-height: 32px !important;
            }
          }
        `}</style>
      </div>
    );
  }

  // Gram bazlı satış için gelişmiş kontrol
  return (
    <div className="unit-selector">
      <div className="d-flex align-items-start justify-content-start">
        <div className="quantity-control d-flex align-items-center border border-gray-200 rounded-4 overflow-hidden bg-white">
          <button
            type="button"
            onClick={handleDecrement}
            className={`quantity-btn quantity-minus d-flex align-items-center justify-content-center border-0 bg-white text-gray-600 hover-bg-main-600 hover-text-white transition-all ${buttonSizeClass}`}
            disabled={quantity <= minimumQuantity}
            style={{ minWidth: '36px', minHeight: '36px' }}
          >
            <i className="ph ph-minus" style={{ fontSize: '14px' }} />
          </button>
          <div className="quantity-display d-flex align-items-center justify-content-center px-3 bg-white border-start border-end border-gray-200">
            <span className="text-gray-900 fw-medium text-sm">
              {saleType === 1 ? 
                (unit === 'kg' ? `${quantity} Kg` : formatQuantityDisplay(quantity).displayText) : 
                `${quantity} Adet`
              }
            </span>
          </div>
          <button
            type="button"
            onClick={handleIncrement}
            className={`quantity-btn quantity-plus d-flex align-items-center justify-content-center border-0 bg-white text-gray-600 hover-bg-main-600 hover-text-white transition-all ${buttonSizeClass}`}
            style={{ minWidth: '36px', minHeight: '36px' }}
          >
            <i className="ph ph-plus" style={{ fontSize: '14px' }} />
          </button>
        </div>
      </div>


      {/* Fiyat Gösterimi */}
      {showPrice && (
        <div className="mt-12">
          <span className="text-main-600 fw-bold" style={{ fontSize: size === 'sm' ? '16px' : '20px' }}>
            ₺{calculateTotalPrice().toFixed(2)}
          </span>
          <span className="text-gray-500 text-sm ms-4">
            (₺{(discountedPrice || price).toFixed(2)}/Kg)
          </span>
        </div>
      )}

      {/* Hata Mesajı */}
      {error && (
        <div className="alert alert-danger alert-sm mt-12 mb-0 py-8 px-12" role="alert">
          <i className="ph ph-warning-circle me-4"></i>
          {error}
        </div>
      )}

      <style jsx>{`
        .unit-selector {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .quantity-control {
          border-radius: 8px !important;
          transition: all 0.2s ease;
          border: 1px solid #e0e0e0 !important;
          background: white !important;
        }

        .quantity-btn {
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
          background: white !important;
          color: #6c757d !important;
        }

        .quantity-btn:hover:not(:disabled) {
          background: var(--main-600, #299E60) !important;
          color: white !important;
        }

        .quantity-btn:active {
          transform: translateY(0);
        }

        .quantity-display {
          min-width: 100px;
          min-height: 36px;
          border-radius: 0;
          border-left: 1px solid #e0e0e0 !important;
          border-right: 1px solid #e0e0e0 !important;
          background: white !important;
        }

        .quantity-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: #f8f9fa !important;
          color: #adb5bd !important;
        }

        .quantity-btn:disabled:hover {
          background: #f8f9fa !important;
          color: #adb5bd !important;
          transform: none !important;
        }

        /* Responsive adjustments */
        @media (max-width: 576px) {
          .quantity-display {
            min-width: 80px;
            font-size: 12px;
          }
          
          .quantity-btn {
            min-width: 32px !important;
            min-height: 32px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default UnitSelector;

